import { useMutation } from "@tanstack/react-query";
import { useState, type ClipboardEvent, type DragEvent } from "react";

import {
  SUPPORT_TICKET_STATUSES,
  type SupportTicketDetail,
  type SupportTicketStatus,
} from "../../shared/support-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Textarea } from "../components/ui/textarea";
import { cn } from "../lib/utils";
import { addSupportMessage, discardSupportDraft, getSupportTicket } from "./support-api";
import { LabeledSelect } from "./support-fields";
import { MAX_REPLY_FILE_BYTES, useComposerFiles } from "./composer-files";
import { ComposerFiles } from "./SupportComposerFiles";
import { SupportSavedReplyPicker } from "./SupportSavedReplyPicker";
import { requesterLabel, STATUS_LABELS } from "./support-format";

type Mode = "reply" | "note";

const defaultNext = (mode: Mode, current: SupportTicketStatus): SupportTicketStatus =>
  mode === "reply" && current !== "solved" ? "waiting_on_customer" : current;

function recipientLine(ticket: SupportTicketDetail, mode: Mode): string {
  if (mode === "note") return "Only your team sees internal notes.";
  const name = requesterLabel(ticket.requester);
  return ticket.requester.email
    ? `To ${name} <${ticket.requester.email}>`
    : `To ${name}, in their workspace only`;
}

function useComposer(
  ticket: SupportTicketDetail,
  onChanged: (ticket: SupportTicketDetail) => void,
) {
  const [mode, setMode] = useState<Mode>("reply");
  const [body, setBody] = useState("");
  const [next, setNext] = useState<SupportTicketStatus>(() => defaultNext("reply", ticket.status));
  const files = useComposerFiles(ticket.id);
  const submit = useMutation({
    mutationFn: async () => {
      await addSupportMessage(ticket.id, {
        kind: mode,
        bodyText: body,
        nextStatus: next,
        ...(files.uploadIds.length > 0 ? { attachmentIds: files.uploadIds } : {}),
      });
      return getSupportTicket(ticket.id);
    },
    onSuccess: (result) => {
      setBody("");
      files.clear();
      setNext(defaultNext(mode, result.ticket.status));
      onChanged(result.ticket);
    },
  });
  const discard = useMutation({
    mutationFn: () => discardSupportDraft(ticket.id),
    onSuccess: (result) => {
      onChanged(result.ticket);
    },
  });
  const choose = (chosen: Mode) => {
    setMode(chosen);
    setNext(defaultNext(chosen, ticket.status));
  };
  return { mode, body, next, setBody, setNext, choose, submit, discard, files };
}

/** Why the files cannot go out with this message yet, if they cannot. */
function filesProblem(composer: ReturnType<typeof useComposer>): string | null {
  if (composer.files.failed) return "Remove the files that could not be attached.";
  if (composer.files.uploading) return null;
  return composer.mode === "reply" && composer.files.readyBytes > MAX_REPLY_FILE_BYTES
    ? "Files on a reply are emailed, so together they can be at most 3.5 MB."
    : null;
}

function composerError(
  composer: ReturnType<typeof useComposer>,
  problem: string | null,
): string | null {
  return composer.submit.error?.message ?? composer.discard.error?.message ?? problem;
}

/** What a reply can start from: a waiting draft, or a saved reply. */
function ReplyStarters({
  ticket,
  composer,
  busy,
}: {
  ticket: SupportTicketDetail;
  composer: ReturnType<typeof useComposer>;
  busy: boolean;
}) {
  return (
    <>
      {ticket.draft ? (
        <DraftBanner
          draft={ticket.draft}
          busy={busy}
          onUse={composer.setBody}
          onDiscard={() => {
            composer.discard.mutate();
          }}
        />
      ) : null}
      <SupportSavedReplyPicker
        ticket={ticket}
        body={composer.body}
        disabled={busy}
        onInsert={composer.setBody}
      />
    </>
  );
}

/** Dropped files and pasted images join the message's files; pasted text pastes as usual. */
function fileHandlers(add: (files: readonly File[]) => void) {
  return {
    onPaste: (event: ClipboardEvent<HTMLFormElement>) => {
      const pasted = [...event.clipboardData.files];
      if (pasted.length === 0) return;
      event.preventDefault();
      add(pasted);
    },
    onDragOver: (event: DragEvent<HTMLFormElement>) => {
      event.preventDefault();
    },
    onDrop: (event: DragEvent<HTMLFormElement>) => {
      event.preventDefault();
      add([...event.dataTransfer.files]);
    },
  };
}

/**
 * The reply box. A person writes and sends every reply (ADR-0089); a draft saved by a workflow or
 * an AI client waits here until someone uses or discards it.
 */
export function SupportComposer({
  ticket,
  onChanged,
}: {
  ticket: SupportTicketDetail;
  onChanged: (ticket: SupportTicketDetail) => void;
}) {
  const composer = useComposer(ticket, onChanged);
  const { mode, submit, discard, files } = composer;
  const busy = submit.isPending || discard.isPending;
  const problem = filesProblem(composer);
  const error = composerError(composer, problem);
  return (
    <form
      aria-label={mode === "reply" ? "Reply to the customer" : "Add an internal note"}
      className={cn(
        "flex flex-col gap-3 rounded-xl px-5 py-4 shadow-card",
        mode === "note" ? "bg-warning-background" : "bg-card",
      )}
      onSubmit={(event) => {
        event.preventDefault();
        submit.mutate();
      }}
      {...fileHandlers(files.add)}
    >
      <ModeChoice mode={mode} onChoose={composer.choose} />
      <p className="text-sm text-muted-foreground">{recipientLine(ticket, mode)}</p>
      {mode === "reply" ? <ReplyStarters ticket={ticket} composer={composer} busy={busy} /> : null}
      <ComposerBody mode={mode} value={composer.body} busy={busy} onChange={composer.setBody} />
      <ComposerFiles
        files={files.files}
        disabled={busy}
        full={files.full}
        onAdd={files.add}
        onRemove={files.remove}
      />
      {error ? <FieldError>{error}</FieldError> : null}
      <ComposerFooter
        mode={mode}
        status={composer.next}
        busy={busy || files.uploading || problem !== null}
        pending={submit.isPending}
        empty={composer.body.trim() === ""}
        onNext={composer.setNext}
      />
    </form>
  );
}

function ComposerBody({
  mode,
  value,
  busy,
  onChange,
}: {
  mode: Mode;
  value: string;
  busy: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <>
      <label htmlFor="support-composer" className="sr-only">
        {mode === "reply" ? "Reply" : "Internal note"}
      </label>
      <Textarea
        id="support-composer"
        rows={6}
        value={value}
        disabled={busy}
        maxLength={100_000}
        placeholder={mode === "reply" ? "Write a reply…" : "Write an internal note…"}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </>
  );
}

function ComposerFooter({
  mode,
  status,
  busy,
  pending,
  empty,
  onNext,
}: {
  mode: Mode;
  status: SupportTicketStatus;
  busy: boolean;
  pending: boolean;
  empty: boolean;
  onNext: (status: SupportTicketStatus) => void;
}) {
  return (
    <div className="flex flex-wrap items-end justify-end gap-3">
      <div className="w-56">
        <LabeledSelect
          id="support-next-status"
          label="Then set status to"
          value={status}
          disabled={busy}
          options={SUPPORT_TICKET_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] }))}
          onChange={(value) => {
            onNext(value as SupportTicketStatus);
          }}
        />
      </div>
      <Button type="submit" disabled={busy || empty}>
        {pending ? "Saving…" : mode === "reply" ? "Send reply" : "Add note"}
      </Button>
    </div>
  );
}

function ModeChoice({ mode, onChoose }: { mode: Mode; onChoose: (mode: Mode) => void }) {
  return (
    <div role="radiogroup" aria-label="Message type" className="flex gap-2">
      {(["reply", "note"] as const).map((value) => (
        <Button
          key={value}
          type="button"
          role="radio"
          aria-checked={mode === value}
          variant={mode === value ? "secondary" : "ghost"}
          onClick={() => {
            onChoose(value);
          }}
        >
          {value === "reply" ? "Public reply" : "Internal note"}
        </Button>
      ))}
    </div>
  );
}

function DraftBanner({
  draft,
  busy,
  onUse,
  onDiscard,
}: {
  draft: NonNullable<SupportTicketDetail["draft"]>;
  busy: boolean;
  onUse: (text: string) => void;
  onDiscard: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-info-background px-4 py-2.5">
      <span className="text-sm">
        {draft.sourceKind === "workflow" ? "AI drafted a reply" : "A draft reply is ready"} (
        {draft.sourceLabel}). Nothing is sent until you send it.
      </span>
      <span className="flex gap-2">
        <Button type="button" variant="ghost" disabled={busy} onClick={onDiscard}>
          Discard
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => {
            onUse(draft.bodyText);
          }}
        >
          Use draft
        </Button>
      </span>
    </div>
  );
}
